import React, { useState } from 'react';
import {
  usePermissions,
  useTranslate,
  ListController,
  ListContextProvider,
  useListContext,
} from 'react-admin';
import { ListView } from '~/layout';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import { NoPermissions } from '~/components/ra';
import {
  AccordionSummary,
  AccordionDetails,
  EditButton,
  TextField,
  AceScriptEditor,
} from '~/components';
import ScriptEdit from './components/ScriptEdit';
import { Script } from '~/types/scripts';
import { time } from '~/utils';

const label = (key: string) => `resources.scripts.fields.${key}`;

const AccordionSummaryStyled = withStyles({
  root: {
    '&.Mui-focused': {
      backgroundColor: 'inherit',
    },
  },
})((props: any) => <AccordionSummary {...props} />);

const ScriptsList = (props: any) => {
  const { permissions } = usePermissions();
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.scripts.${key}`);

  if (!permissions) return null;
  if (!permissions?.includes('script.list')) return <NoPermissions />;

  return (
    <ListController {...props}>
      {(controllerProps: any) => (
        <ListContextProvider value={controllerProps}>
          <ListView
            title={
              <div className={classes.headline}>
                <Typography variant='h2'>{t('name')}</Typography>
              </div>
            }
            {...controllerProps}
            pagination={controllerProps.loading || controllerProps.total === 0 ? null : undefined}
            bulkActionButtons={false}
            actions={<></>}
          >
            <ListBody />
          </ListView>
        </ListContextProvider>
      )}
    </ListController>
  );
};

const ListBody: React.FC = () => {
  const { ids, data, loading, total } = useListContext();
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string): string => translate(label(key));
  const classes = useListBodyStyles();

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [editRecord, setEditRecord] = useState<Script | null>(null);

  const handleToggle = (key: string, isExpanded: boolean) => () => {
    setExpanded((prev) => ({ ...prev, [key]: !isExpanded }));
  };

  if (loading) {
    return <div className={classes.content}>{t('searchInProgress')}</div>;
  }
  if (!total) {
    return <div className={classes.content}>{t('noResultsFound')}</div>;
  }
  return (
    <>
      <ScriptEdit record={editRecord} onClose={() => setEditRecord(null)} />
      <div className={classes.list}>
        {(ids as string[]).map((id) => {
          const script = data[id] as Script;
          const isExpanded = Boolean(expanded[id]);

          return (
            <Grid key={id} item xs={12}>
              <Accordion expanded={isExpanded} onChange={handleToggle(id, isExpanded)}>
                <AccordionSummaryStyled
                  aria-controls={`${id}-content`}
                  id={`${id}-header`}
                  expanded={isExpanded}
                  heading={
                    <Typography variant='body1'>
                      {script.extensionPoint} - {script?.description}
                    </Typography>
                  }
                  actionNode={
                    <Grid
                      container
                      justify='flex-end'
                      alignItems='center'
                      spacing={1}
                      wrap='nowrap'
                    >
                      {permissions?.includes('script.update') && (
                        <Grid item>
                          <EditButton
                            onClick={() => {
                              setEditRecord(script);
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  }
                  actionAlwaysVisible
                />
                <AccordionDetails>
                  {isExpanded && (
                    <Grid container spacing={2} className={classes.details}>
                      {script?.extensionPoint && (
                        <Grid item xs={4}>
                          <TextField label={label('extensionPoint')} multiline>
                            {script.extensionPoint}
                          </TextField>
                        </Grid>
                      )}
                      {script?.description && (
                        <Grid item xs={4}>
                          <TextField label={label('description')} multiline>
                            {script?.description}
                          </TextField>
                        </Grid>
                      )}
                      {script?.updated && (
                        <Grid item xs={4}>
                          <TextField label={label('updatedAt')}>
                            {time(script?.updated).format('LL LT')}
                          </TextField>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant='caption' color='textSecondary'>
                          {t('data')}
                        </Typography>
                        <AceScriptEditor
                          value={script.data}
                          onChange={() => {}}
                          height='400px'
                          readOnly
                        />
                      </Grid>
                    </Grid>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
      </div>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  headline: {
    marginTop: theme.spacing(1),
    display: 'flex',
    marginBottom: theme.spacing(2),
  },
}));

const useListBodyStyles = makeStyles((theme) => ({
  content: {
    fontSize: 14,
    paddingLeft: 16,
    paddingRight: 16,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: 24,
      paddingRight: 24,
    },
    paddingTop: 16,
    paddingBottom: 16,
  },
  list: {
    paddingLeft: 16,
    paddingRight: 16,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: 24,
      paddingRight: 24,
    },
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  details: {
    padding: theme.spacing(1),
  },
  code: {
    margin: theme.spacing(0.5, 0),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    overflowX: 'auto',
    fontSize: '0.8rem',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

export default ScriptsList;
